import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DefaultAvatar } from "../../../../../components/common/DefaultAvatar";
import { formatPhoneForDisplay } from "../../../../../utils";

import { formatDateRu, getDaysLeft } from "../../../utils/date";
import {
    getStatusTone,
    pickCreatedAt,
    pickExpiresAt,
    pickListingsCount,
    pickPlanCode,
    safeString,
} from "../../../utils/user";

import type { SortKey } from "../../../types";
import {
    CreditCardIcon,
    IconBtn,
    KeyIcon,
    PencilIcon,
    PlanPill,
    RefreshIcon,
    SortableTh,
    StatusDot,
    TrashIcon,
} from "../UI";

type Props = {
    variant: "users" | "subscribers";
    users: any[];
    pending?: boolean;
    sortKey: SortKey;
    sortDir: "asc" | "desc";
    onSortClick: (key: SortKey) => void;
    actionsDisabled?: boolean;
    expiringDays: number;
    onEditProfile?: (user: any) => void;
    onResetPassword?: (user: any) => void;
    onCreateSubscription?: (user: any) => void;
    onToggleListingAccess?: (user: any) => void;
    onDeleteUser?: (user: any) => void;
    listingAccessEmails?: Set<string>;
    listingAccessPending?: boolean;
    listingAccessActionEmail?: string | null;
};

const DesktopView: React.FC<Props> = ({
    variant,
    users,
    pending,
    sortKey,
    sortDir,
    onSortClick,
    actionsDisabled = false,
    expiringDays,
    onEditProfile,
    onResetPassword,
    onCreateSubscription,
    onToggleListingAccess,
    onDeleteUser,
    listingAccessEmails,
    listingAccessPending,
    listingAccessActionEmail,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const isUsersView = variant === "users";

    const openUser = (user: any) => {
        if (user?.id == null) return;
        sessionStorage.setItem("control-panel:return-scroll-y", String(window.scrollY));
        navigate(`/control-panel/users/${encodeURIComponent(String(user.id))}`, {
            state: { controlPanelReturn: true, controlPanelPath: `${location.pathname}${location.search}` },
        });
    };

    return (
        <div className="hidden overflow-x-auto xl:block">
            <table className="min-w-[1110px] w-full text-left text-[13px]">
                <thead className="border-y border-slate-200 bg-slate-50 text-slate-500">
                    <tr>
                        <SortableTh label="Пользователь" colKey="name" sortKey={sortKey} sortDir={sortDir} onClick={() => onSortClick("name")} />
                        <SortableTh label="Контакты" colKey="email" sortKey={sortKey} sortDir={sortDir} onClick={() => onSortClick("email")} />
                        <SortableTh label="Объявления" colKey="listingsCount" sortKey={sortKey} sortDir={sortDir} onClick={() => onSortClick("listingsCount")} />
                        <th className="px-3 py-3 text-xs font-medium">Тариф</th>
                        {isUsersView ? <th className="px-3 py-3 text-xs font-medium">Доступ к маркету</th> : null}
                        <SortableTh
                            label={isUsersView ? "Регистрация" : "Подписка до"}
                            colKey={isUsersView ? "createdAt" : "expiresAt"}
                            sortKey={sortKey}
                            sortDir={sortDir}
                            onClick={() => onSortClick(isUsersView ? "createdAt" : "expiresAt")}
                        />
                        <th className="px-4 py-3 text-right text-xs font-medium">Действия</th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 text-slate-700">
                    {users.map((user: any, index: number) => {
                        const name = safeString(user?.name) || "Без имени";
                        const email = safeString(user?.email);
                        const phone = formatPhoneForDisplay(user?.phone);
                        const planCode = pickPlanCode(user);
                        const listingsCount = pickListingsCount(user);
                        const normalizedEmail = email.trim().toLowerCase();
                        const hasListingAccess = Boolean(normalizedEmail && listingAccessEmails?.has(normalizedEmail));
                        const listingAccessActionPending = listingAccessActionEmail === normalizedEmail;
                        const expiresAt = pickExpiresAt(user);
                        const createdAt = pickCreatedAt(user);
                        const daysLeft = user?._ui?.daysLeft ?? getDaysLeft(expiresAt);
                        const statusTone = getStatusTone(daysLeft, expiringDays);
                        const dateLabel = isUsersView
                            ? (createdAt ? formatDateRu(createdAt) : "—")
                            : (expiresAt ? formatDateRu(expiresAt) : "—");

                        return (
                            <tr
                                key={String(user?.id ?? index)}
                                className="group cursor-pointer bg-white transition-colors hover:bg-[#fff8fc] focus-within:bg-[#fff8fc]"
                                onClick={() => openUser(user)}
                            >
                                <td className="px-4 py-3.5">
                                    <div className="flex min-w-[230px] items-center gap-3">
                                        <DefaultAvatar name={name} color={user?.avatarColor} height={38} width={38} />
                                        <div className="min-w-0">
                                            <div className="font-semibold text-slate-900">{name}</div>
                                            <div className="mt-0.5 text-xs text-slate-500">ID {user?.id ?? "—"}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-3 py-3.5">
                                    <div className="min-w-[190px] break-all text-slate-700">{email || "—"}</div>
                                    <div className="mt-0.5 text-xs text-slate-500">{phone || "Телефон не указан"}</div>
                                </td>
                                <td className="px-3 py-3.5">
                                    <div className="flex items-center gap-2">
                                        <span className="text-lg font-semibold tabular-nums text-slate-900">{listingsCount ?? "—"}</span>
                                        <span className="text-xs text-slate-500">объявл.</span>
                                    </div>
                                </td>
                                <td className="px-3 py-3.5"><PlanPill value={planCode} /></td>
                                {isUsersView ? (
                                    <td className="px-3 py-3.5">
                                        <span className={[
                                            "inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold",
                                            hasListingAccess
                                                ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                                                : "border-slate-200 bg-slate-50 text-slate-500",
                                        ].join(" ")}>
                                            {hasListingAccess ? "Разрешён" : "Закрыт"}
                                        </span>
                                    </td>
                                ) : null}
                                <td className="px-3 py-3.5">
                                    <div className="flex items-center gap-2 whitespace-nowrap tabular-nums text-slate-700">
                                        {!isUsersView ? <StatusDot tone={statusTone} /> : null}
                                        {dateLabel}
                                    </div>
                                </td>
                                <td
                                    className="px-4 py-3.5"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <div className="flex items-center justify-end gap-1.5">
                                        <IconBtn title="Редактировать профиль" onClick={onEditProfile ? () => onEditProfile(user) : undefined} disabled={actionsDisabled}><PencilIcon /></IconBtn>
                                        <IconBtn title="Отправить сброс пароля" onClick={onResetPassword ? () => onResetPassword(user) : undefined} disabled={actionsDisabled}><RefreshIcon /></IconBtn>
                                        <IconBtn title="Управление подпиской" tone="accent" onClick={onCreateSubscription ? () => onCreateSubscription(user) : undefined} disabled={actionsDisabled}><CreditCardIcon /></IconBtn>
                                        {isUsersView ? (
                                            <IconBtn
                                                title={hasListingAccess ? "Запретить создание объявлений" : "Разрешить создание объявлений"}
                                                tone={hasListingAccess ? "accent" : "default"}
                                                onClick={onToggleListingAccess ? () => onToggleListingAccess(user) : undefined}
                                                disabled={actionsDisabled || listingAccessPending || listingAccessActionPending || !normalizedEmail}
                                            ><KeyIcon /></IconBtn>
                                        ) : null}
                                        <IconBtn title="Удалить пользователя" tone="danger" onClick={onDeleteUser ? () => onDeleteUser(user) : undefined} disabled={actionsDisabled}><TrashIcon /></IconBtn>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
            {pending ? <div className="px-4 py-4 text-sm text-slate-500">Обновляем список…</div> : null}
            {!users.length && !pending ? <div className="px-4 py-8 text-center text-sm text-slate-500">Пользователи не найдены</div> : null}
        </div>
    );
};

export default DesktopView;
